import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Spinner } from '@tkpod/ui';
import { useApiClient } from '@tkpod/api-client';
import { CategoryTree } from '@/components/catalog/CategoryTree';
import { ProductCard } from '@/components/catalog/ProductCard';
import {
  CatalogSearchFilter,
  type ActiveFilter,
  type FilterGroup,
} from '@/components/catalog/CatalogSearchFilter';

interface Category {
  id: string;
  name: string;
  slug: string;
  templateCount: number;
  children: Category[];
}

interface CatalogProduct {
  id: string;
  name: string;
  slug: string;
  brand: string | null;
  modelName: string | null;
  productType: string;
  thumbnailUrl: string | null;
  images: Array<{ url: string; thumbnailUrl: string; isDefault: boolean }> | null;
  categoryName: string | null;
  optionSummary: string | null;
  startingPrice: string | null;
  variantCount: number;
  /**
   * UPT design surface. Drives the card footer copy: PRODUCT templates create
   * a fixed-design product, STOREFRONT templates let buyers personalize.
   */
  designContext: 'PRODUCT' | 'STOREFRONT' | string | null;
}

interface CatalogResponse {
  data: CatalogProduct[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface FilterFacets {
  brands: string[];
  types: string[];
  tags: string[];
  categories: { id: string; name: string; slug: string }[];
  options: { id: string; name: string; slug: string; displayName: string }[];
}

export function CatalogPage() {
  const api = useApiClient();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [page, setPage] = useState(1);
  const limit = 12;

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    return () => {
      clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, 300);
  }, []);

  const filtersQuery = useQuery({
    queryKey: ['catalog', 'filters'],
    queryFn: () => api.get<{ data: FilterFacets }>('/api/v1/catalog/filters'),
    staleTime: 5 * 60 * 1000,
  });

  const categoriesQuery = useQuery({
    queryKey: ['catalog', 'categories'],
    queryFn: () => api.get<{ data: Category[] }>('/api/v1/catalog/categories'),
  });

  const activeCategory = activeFilters.find((f) => f.key === 'category');
  const activeBrand = activeFilters.find((f) => f.key === 'brand');
  const activeTag = activeFilters.find((f) => f.key === 'tag');
  const activeType = activeFilters.find((f) => f.key === 'type');

  const effectiveCategoryId = activeCategory?.value ?? selectedCategoryId;

  const queryParams = new URLSearchParams();
  queryParams.set('page', String(page));
  queryParams.set('limit', String(limit));
  if (debouncedSearch) queryParams.set('search', debouncedSearch);
  if (effectiveCategoryId) queryParams.set('categoryId', effectiveCategoryId);
  if (activeBrand?.value) queryParams.set('brand', activeBrand.value);
  if (activeTag?.value) queryParams.set('tag', activeTag.value);
  if (activeType?.value) queryParams.set('type', activeType.value);

  const productsQuery = useQuery({
    queryKey: ['catalog', 'products', page, debouncedSearch, effectiveCategoryId, activeBrand?.value, activeTag?.value, activeType?.value],
    queryFn: () =>
      api.get<CatalogResponse>(`/api/v1/catalog/templates?${queryParams.toString()}`),
  });

  const categories = categoriesQuery.data?.data ?? [];
  const products = productsQuery.data?.data ?? [];
  const pagination = productsQuery.data?.pagination;
  const facets = filtersQuery.data?.data;

  const filterGroups: FilterGroup[] = useMemo(() => {
    if (!facets) return [];
    const groups: FilterGroup[] = [];

    if (facets.categories.length > 0) {
      groups.push({
        key: 'category',
        label: 'Category',
        options: facets.categories.map((c) => ({ id: c.id, label: c.name })),
      });
    }

    if (facets.brands.length > 0) {
      groups.push({
        key: 'brand',
        label: 'Brand',
        options: facets.brands.map((b) => ({ id: b, label: b })),
      });
    }

    if (facets.tags.length > 0) {
      groups.push({
        key: 'tag',
        label: 'Tag',
        options: facets.tags.map((t) => ({ id: t, label: t })),
      });
    }

    if (facets.types.length > 0) {
      groups.push({
        key: 'type',
        label: 'Type',
        options: facets.types.map((t) => ({
          id: t,
          label: t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        })),
      });
    }

    for (const opt of facets.options) {
      groups.push({
        key: `option_${opt.slug}`,
        label: opt.displayName,
        options: [],
      });
    }

    return groups;
  }, [facets]);

  const handleFilterAdd = useCallback(
    (filter: ActiveFilter) => {
      setActiveFilters((prev) => {
        const next = prev.filter((f) => f.key !== filter.key);
        if (filter.value) next.push(filter);
        return next;
      });
      if (filter.key === 'category') {
        setSelectedCategoryId(filter.value || null);
      }
      setPage(1);
    },
    [],
  );

  const handleFilterRemove = useCallback(
    (key: string) => {
      setActiveFilters((prev) => prev.filter((f) => f.key !== key));
      if (key === 'category') {
        setSelectedCategoryId(null);
      }
      setPage(1);
    },
    [],
  );

  const handleFiltersClear = useCallback(() => {
    setActiveFilters([]);
    setSelectedCategoryId(null);
    setPage(1);
  }, []);

  const findCategoryById = useCallback(
    (cats: Category[], id: string): Category | null => {
      for (const cat of cats) {
        if (cat.id === id) return cat;
        const found = findCategoryById(cat.children, id);
        if (found) return found;
      }
      return null;
    },
    [],
  );

  const handleCategorySelect = (catId: string | null) => {
    setSelectedCategoryId(catId);
    if (catId) {
      const cat = findCategoryById(categories, catId);
      handleFilterAdd({
        key: 'category',
        label: 'Category',
        value: catId,
        displayValue: cat?.name ?? '',
      });
    } else {
      handleFilterRemove('category');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold text-text-primary">Product Catalog</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Browse our product catalog and find the right product for your order.
        </p>
      </div>

      <div className="flex gap-6">
        {/* Category Sidebar */}
        <div className="hidden w-56 shrink-0 lg:block">
          <Card padding="sm">
            <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-text-tertiary">
              Categories
            </p>
            {categoriesQuery.isLoading ? (
              <div className="flex justify-center py-4">
                <Spinner size="sm" />
              </div>
            ) : (
              <CategoryTree
                categories={categories}
                selectedId={selectedCategoryId}
                onSelect={handleCategorySelect}
              />
            )}
          </Card>
        </div>

        {/* Main Content */}
        <div className="min-w-0 flex-1">
          {/* Shopify-style Search & Filters */}
          <div className="mb-6">
            <CatalogSearchFilter
              search={search}
              onSearchChange={handleSearchChange}
              activeFilters={activeFilters}
              onFilterAdd={handleFilterAdd}
              onFilterRemove={handleFilterRemove}
              onFiltersClear={handleFiltersClear}
              filterGroups={filterGroups}
            />
          </div>

          {/* Products Grid */}
          {productsQuery.isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Spinner size="lg" />
            </div>
          ) : productsQuery.isError ? (
            <div className="py-20 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-error-50">
                <svg className="h-6 w-6 text-error-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-text-primary">Failed to load products</p>
              <p className="mt-1 text-sm text-text-tertiary">
                Something went wrong. Please try again.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => void productsQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          ) : products.length === 0 ? (
            <div className="py-20 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-tertiary">
                <svg className="h-6 w-6 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-text-primary">No products found</p>
              <p className="mt-1 text-sm text-text-tertiary">
                {debouncedSearch || activeFilters.length > 0
                  ? 'No results match your search or filters. Try adjusting them.'
                  : 'No products available in this category.'}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    name={product.name}
                    slug={product.slug}
                    brand={product.brand}
                    modelName={product.modelName}
                    thumbnailUrl={product.images?.find((i) => i.isDefault)?.thumbnailUrl ?? product.images?.[0]?.thumbnailUrl ?? product.thumbnailUrl}
                    optionSummary={product.optionSummary}
                    startingPrice={product.startingPrice}
                    type={product.productType}
                    designContext={product.designContext}
                    onClick={() => navigate(`/catalog/${product.slug}`)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-tertiary disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span className="text-sm text-text-secondary">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    type="button"
                    className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-tertiary disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
